import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheck } from 'react-icons/fa';
import U2000BasicDialog from '../../../../u2000Core/components/dialog/U2000BasicDialog';
import { NS } from '../../../U3141';

/** Ordre stable, aligné sur U3141_CHAIN_PDF_PROGRESS_STEPS (backend). */
export const U3141_CHAIN_IMPORT_STEPS = [
  'preparation',
  'lecture_pdf',
  'analyse',
  'extraction_llm',
  'ecriture_chaine',
  'decoupage_index',
  'annotations',
  'fusion',
  'finalisation',
] as const;

/** Étapes job fusion chaîne annotée (BL-0704). */
export const U3141_ANNOTATED_LOT_CHAIN_MERGE_STEPS = ['preparation', 'fusion', 'finalisation'] as const;

/** Sous-ensemble d'étapes job import annotations seules (BL-0700). */
export const U3141_CHAIN_ANNOTATIONS_IMPORT_STEPS = [
  'preparation',
  'lecture_pdf',
  'analyse',
  'annotations',
  'fusion',
  'finalisation',
] as const;

/** Ordre stable, aligné sur U3141_CADASTRAL_HISTORY_PROGRESS_STEPS (backend, BL-0593). */
export const U3141_HISTORIQUE_PROGRESS_STEPS = [
  'preparation',
  'recuperation_rapport',
  'enregistrement',
  'extraction_llm',
  'ecriture_chaine',
  'finalisation',
] as const;

const closeButtonClass = `inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-xs transition-all duration-200
  hover:bg-gray-50 hover:border-gray-400 hover:text-gray-900 hover:shadow-md
  focus:outline-hidden focus:ring-2 focus:ring-gray-500 focus:ring-offset-2`;

export interface U3141ChainImportProgressDialogProps {
  isOpen: boolean;
  percent: number;
  step: string | null;
  current?: number;
  total?: number;
  detail?: string;
  errorMessage?: string | null;
  onClose: () => void;
  /** Liste ordonnée des étapes (défaut : import de chaîne). */
  steps?: readonly string[];
  titleKey?: string;
  introKey?: string;
  closeHintKey?: string;
  /** Préfixe i18n des libellés d'étape, ex. lblChainImportStep ou lblHistoriqueProgressStep */
  stepLabelPrefix?: string;
}

export const U3141ChainImportProgressDialog: React.FC<U3141ChainImportProgressDialogProps> = ({
  isOpen,
  percent,
  step,
  current,
  total,
  detail,
  errorMessage,
  onClose,
  steps = U3141_CHAIN_IMPORT_STEPS,
  titleKey = 'dlgChainImportProgressTitle',
  introKey = 'dlgChainImportProgressIntro',
  closeHintKey = 'dlgChainImportProgressCloseHint',
  stepLabelPrefix = 'lblChainImportStep',
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  const activeStep = step && steps.includes(step) ? step : steps[0] ?? 'preparation';
  const activeIndex = steps.indexOf(activeStep);
  const safePercent = Math.max(0, Math.min(100, percent));
  const hasCount = typeof current === 'number' && typeof total === 'number' && total > 0;
  const detailLabel =
    detail != null
      ? t(`${stepLabelPrefix}.detail.${detail}`, {
          ns: NS,
          defaultValue: detail,
        })
      : undefined;

  return (
    <U2000BasicDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t(titleKey, { ns: NS })}
      width="medium"
      height="max-h-[90vh]"
    >
      <div className="flex flex-col h-full focus:outline-hidden">
        <section className="flex-1 px-6 py-4 overflow-y-auto min-h-0">
          <p className="text-sm text-gray-700">{t(introKey, { ns: NS })}</p>
          <div className="mt-4 h-2 w-full rounded-sm bg-gray-200">
            <div
              className="h-2 rounded-sm bg-green-600 transition-all duration-300"
              style={{ width: `${safePercent}%` }}
            />
          </div>
          <p className="mt-1 text-xs text-gray-600">{t('dlgChainImportProgressPercent', { ns: NS, percent: safePercent })}</p>
          <ol className="mt-4 space-y-2">
            {steps.map((stepId, index) => {
              const done = !errorMessage && index < activeIndex;
              const currentStep = !errorMessage && index === activeIndex;
              return (
                <li key={stepId} className="flex items-start gap-3 text-sm">
                  <span
                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
                      done
                        ? 'border-green-600 bg-green-600 text-white'
                        : currentStep
                          ? 'border-green-600 bg-white'
                          : 'border-gray-300 bg-white'
                    }`}
                    aria-hidden
                  >
                    {done ? <FaCheck className="h-3 w-3" /> : currentStep ? <span className="h-2 w-2 rounded-full bg-green-600" /> : null}
                  </span>
                  <span className={currentStep ? 'font-medium text-gray-900' : done ? 'text-gray-700' : 'text-gray-400'}>
                    {t(`${stepLabelPrefix}.${stepId}`, { ns: NS })}
                    {currentStep && detailLabel ? (
                      <span className="mt-0.5 block text-xs font-normal text-gray-600">
                        {hasCount
                          ? t('dlgChainImportProgressDetail', { ns: NS, detail: detailLabel, current, total })
                          : t('dlgChainImportProgressDetailOnly', { ns: NS, detail: detailLabel })}
                      </span>
                    ) : null}
                    {currentStep && !detailLabel && hasCount ? (
                      <span className="mt-0.5 block text-xs font-normal text-gray-600">
                        {t('dlgChainImportProgressCount', { ns: NS, current, total })}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ol>
          {errorMessage ? <p className="mt-4 text-sm text-red-700">{errorMessage}</p> : null}
          <p className="mt-4 text-xs text-gray-500">{t(closeHintKey, { ns: NS })}</p>
        </section>
        <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-6 py-4">
          <button type="button" className={closeButtonClass} onClick={onClose}>
            {t('btnChainImportProgressClose', { ns: NS })}
          </button>
        </footer>
      </div>
    </U2000BasicDialog>
  );
};
